import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Styles from "./styles.module.scss";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { getWalletHistory } from "../../../store/cabinet/action";

const shortenAddress = (address) => {
    if (!address) return "—";

    return `${address.slice(0, 6)}...${address.slice(-4)}`;
};

const formatCryptoAmount = (value, precision = 8) => {
    const numberValue = Number(value);

    if (!Number.isFinite(numberValue)) return "0";

    return new Intl.NumberFormat("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: precision,
    }).format(numberValue);
};

const formatAtomicAmount = (value, decimals = 18, precision = 8) => {
    const numberValue = Number(value);

    if (!Number.isFinite(numberValue) || numberValue <= 0) return "0";

    const decimalValue = numberValue / Math.pow(10, decimals);

    return formatCryptoAmount(decimalValue, precision);
};

const formatDate = (date) => {
    if (!date) return "—";

    const dateValue = new Date(date);

    if (Number.isNaN(dateValue.getTime())) return "—";

    return new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "2-digit",
        year: "numeric",
    }).format(dateValue);
};

const getDirectionLabel = (direction, t) => {
    if (direction === "in") return t("Получено");
    if (direction === "out") return t("Отправлено");

    return direction || "—";
};

const getStatusLabel = (status, t) => {
    switch (status) {
        case "confirmed":
            return t("Выполнено");
        case "broadcast":
            return t("В сети");
        case "pending":
            return t("В обработке");
        case "failed":
            return t("Ошибка");
        case "canceled":
        case "cancelled":
            return t("Отменено");
        default:
            return status || "—";
    }
};

const getStatusClass = (status) => {
    switch (status) {
        case "confirmed":
            return "statusCompleted";
        case "broadcast":
        case "pending":
            return "statusPending";
        case "failed":
            return "statusFailed";
        case "canceled":
        case "cancelled":
            return "statusCanceled";
        default:
            return "statusPending";
    }
};

const getHistoryItems = (walletHistory) => {
    if (Array.isArray(walletHistory)) return walletHistory;
    if (Array.isArray(walletHistory?.items)) return walletHistory.items;

    return [];
};

const CabinetWalletHistory = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const walletHistoryRefreshKey = useSelector(store => store.cabinet.walletHistoryRefreshKey);

    const loadingRef = useRef(false);
    const requestKeyRef = useRef(0);
    const handledRefreshKeyRef = useRef(walletHistoryRefreshKey);

    const [typeFilter, setTypeFilter] = useState("all");
    const [historyItems, setHistoryItems] = useState([]);
    const [historyMeta, setHistoryMeta] = useState(null);
    const [isLoadingMore, setIsLoadingMore] = useState(false);

    const historyList = useMemo(() => {
        return [...historyItems].sort((a, b) => {
            return new Date(b?.created_at).getTime() - new Date(a?.created_at).getTime();
        });
    }, [historyItems]);

    const loadHistory = useCallback(async ({
                                               page = 1,
                                               shouldReset = false,
                                               requestKey = requestKeyRef.current,
                                           } = {}) => {
        if (loadingRef.current) return;

        loadingRef.current = true;
        setIsLoadingMore(true);

        const response = await dispatch(getWalletHistory(typeFilter, page));

        if (requestKeyRef.current !== requestKey) {
            loadingRef.current = false;
            setIsLoadingMore(false);
            return;
        }

        const nextItems = getHistoryItems(response);

        setHistoryMeta(response?.meta || null);

        setHistoryItems((prevItems) => {
            if (shouldReset) return nextItems;

            const existingIds = new Set(prevItems.map(item => item.id));
            const uniqueItems = nextItems.filter(item => !existingIds.has(item.id));

            return [...prevItems, ...uniqueItems];
        });

        loadingRef.current = false;
        setIsLoadingMore(false);
    }, [dispatch, typeFilter]);

    useEffect(() => {
        const nextRequestKey = requestKeyRef.current + 1;

        requestKeyRef.current = nextRequestKey;
        loadingRef.current = false;

        setHistoryItems([]);
        setHistoryMeta(null);

        loadHistory({
            page: 1,
            shouldReset: true,
            requestKey: nextRequestKey,
        });
    }, [typeFilter, loadHistory]);

    useEffect(() => {
        if (!walletHistoryRefreshKey) return;
        if (handledRefreshKeyRef.current === walletHistoryRefreshKey) return;

        handledRefreshKeyRef.current = walletHistoryRefreshKey;

        const nextRequestKey = requestKeyRef.current + 1;

        requestKeyRef.current = nextRequestKey;
        loadingRef.current = false;

        setHistoryItems([]);
        setHistoryMeta(null);

        loadHistory({
            page: 1,
            shouldReset: true,
            requestKey: nextRequestKey,
        });
    }, [walletHistoryRefreshKey, loadHistory]);

    const scrollHandler = (event) => {
        const element = event.currentTarget;

        const isBottom =
            element.scrollHeight - element.scrollTop - element.clientHeight < 40;

        const currentPage = historyMeta?.current_page || 1;
        const lastPage = historyMeta?.last_page || 1;

        if (!isBottom) return;
        if (isLoadingMore) return;
        if (currentPage >= lastPage) return;

        loadHistory({
            page: currentPage + 1,
        });
    };

    return (
        <div className={Styles.container}>
            <div className={`${Styles.contentWrap} ${Styles.cabinetCard}`}>
                <div className={Styles.titleRow}>
                    <div className={Styles.title}>
                        {t("Последние транзакции")}
                    </div>

                    <div className={Styles.filtersWrap}>
                        <div className={Styles.filterSelectWrap}>
                            <select
                                value={typeFilter}
                                onChange={(event) => setTypeFilter(event.target.value)}
                            >
                                <option value="all">{t("Тип")}</option>
                                <option value="in">{t("Получено")}</option>
                                <option value="out">{t("Отправлено")}</option>
                            </select>
                        </div>
                    </div>
                </div>

                <div className={Styles.tableHead}>
                    <div>{t("Детали")}</div>
                    <div>{t("Сумма")}</div>
                    <div>{t("Дата")}</div>
                    <div>{t("Статус")}</div>
                </div>

                <div
                    className={Styles.historyScroll}
                    onScroll={scrollHandler}
                >
                    {historyList.length > 0 ? (
                        <>
                            {historyList.map((item) => {
                                const isIncome = item?.direction === "in";
                                const statusClass = getStatusClass(item?.status);
                                const directionLabel = getDirectionLabel(item?.direction, t);
                                const statusLabel = getStatusLabel(item?.status, t);

                                const currencyCode =
                                    item?.currency_code ||
                                    item?.asset?.currency?.code ||
                                    "";

                                const amountLabel = `${isIncome ? "+" : "-"}${formatCryptoAmount(item?.amount_decimal)} ${currencyCode}`;

                                const decimals =
                                    item?.asset?.decimals ||
                                    item?.asset?.currency?.precision ||
                                    18;

                                const feeValue = formatAtomicAmount(item?.fee, decimals);
                                const feeSymbol =
                                    item?.asset?.network?.native_symbol ||
                                    currencyCode;

                                const addressLabel = isIncome
                                    ? `${t("От")}: ${shortenAddress(item?.from_address)}`
                                    : `${t("Кому")}: ${shortenAddress(item?.to_address)}`;

                                const amountSubText = Number(item?.fee) > 0
                                    ? `${t("Комиссия")}: ${feeValue} ${feeSymbol}`
                                    : `${item?.confirmations || 0} ${t("подтверждений")}`;

                                return (
                                    <div
                                        className={Styles.tableRow}
                                        key={item.id}
                                    >
                                        <div className={Styles.detailsCol}>
                                            <div
                                                className={`
                                                    ${Styles.directionIcon}
                                                    ${isIncome ? Styles.directionIconIn : Styles.directionIconOut}
                                                    ${statusClass === "statusCanceled" ? Styles.directionIconCanceled : ""}
                                                `}
                                            >
                                                <svg
                                                    width="22"
                                                    height="22"
                                                    viewBox="0 0 22 22"
                                                    fill="none"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                >
                                                    <path
                                                        d={isIncome ? "M11 3V18" : "M11 19V4"}
                                                        stroke="currentColor"
                                                        strokeWidth="2"
                                                        strokeLinecap="round"
                                                    />

                                                    <path
                                                        d={isIncome ? "M5 12L11 18L17 12" : "M5 10L11 4L17 10"}
                                                        stroke="currentColor"
                                                        strokeWidth="2"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                    />
                                                </svg>
                                            </div>

                                            <div className={Styles.detailsText}>
                                                <div className={Styles.detailsTitle}>
                                                    {directionLabel}
                                                </div>

                                                <div className={Styles.detailsAddress}>
                                                    {addressLabel}
                                                </div>
                                            </div>
                                        </div>

                                        <div className={Styles.amountCol}>
                                            <span className={Styles.mobileLabel}>{t("Сумма")}</span>

                                            <div className={Styles.amountTextWrap}>
                                                <div
                                                    className={`
                                                        ${Styles.amountValue}
                                                        ${isIncome ? Styles.amountPositive : Styles.amountNegative}
                                                        ${statusClass === "statusCanceled" ? Styles.amountCanceled : ""}
                                                    `}
                                                >
                                                    {amountLabel}
                                                </div>

                                                <div className={Styles.amountSubText}>
                                                    {amountSubText}
                                                </div>
                                            </div>
                                        </div>

                                        <div className={Styles.dateCol}>
                                            <span className={Styles.mobileLabel}>{t("Дата")}</span>
                                            <span>{formatDate(item?.created_at)}</span>
                                        </div>

                                        <div className={Styles.statusCol}>
                                            <span className={Styles.mobileLabel}>{t("Статус")}</span>

                                            <span className={`${Styles.statusBadge} ${Styles[statusClass]}`}>
                                                {statusLabel}
                                            </span>
                                        </div>
                                    </div>
                                );
                            })}

                            {isLoadingMore && (
                                <div className={Styles.loadingText}>
                                    {t("Загрузка")}
                                </div>
                            )}
                        </>
                    ) : (
                        <div className={Styles.emptyText}>
                            {isLoadingMore ? t("Загрузка") : t("Транзакции пока не найдены")}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CabinetWalletHistory;